import { createRef, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSegmentTrack } from '@application/hooks/useSegment/useSegment';
import styles from '@application/modules/shared/styles/CampaignDetails/campaigndetails.module.scss';
import { FsVariation } from '@application/modules/flagship/type';
import {
  DetailsSection,
  DetailsTable,
  TableCellCondition,
} from '@src/application/modules/shared/components/Campaigns/DetailsTable/DetailsTable';
import { useFsEnvId } from '@application/modules/flagship/hooks/fsHooks';
import { CopyCode } from '../../CopyCode/CopyCode';

type CampaignDetailsProps = {
  campaignId: string;
  expanded: boolean;
  selectedVariation?: FsVariation;
};

function CodeSample({ flagsValue }: { flagsValue: unknown }) {
  const flagsValueJson = JSON.stringify(flagsValue, null, 4);
  return (
    <>
      <pre className={styles.codeFlags}>
        <code data-testid="code-sample">{flagsValueJson}</code>
      </pre>
      <CopyCode flagsValue={flagsValueJson} />
    </>
  );
}

export function CampaignDetails({ expanded, selectedVariation, campaignId }: CampaignDetailsProps) {
  const details = createRef<HTMLDivElement>();
  const [maxHeight, setMaxHeight] = useState<string>();
  const segmentTrack = useSegmentTrack();
  const { t } = useTranslation();
  const fsEnvId = useFsEnvId();

  const selectedVariations = useMemo(() => {
    return [{ variation: selectedVariation }];
  }, [selectedVariation]);

  useEffect(() => {
    if (details.current) setMaxHeight(`${details.current?.scrollHeight}px`);
  }, [details.current, expanded]);

  return (
    <div
      className={classNames(styles.detailsContainer, { [styles.expanded]: expanded })}
      style={expanded ? { maxHeight } : {}}
      ref={details}
    >
      <div className={styles['flex-5']} />
      <div className={styles['flex-95']}>
        <div>
          <DetailsTable>
            <DetailsSection title={t('Configuration')} array={selectedVariations}>
              {({ variation }) => (
                <TableCellCondition className={styles.codeContainer}>
                  <CodeSample flagsValue={variation?.modifications?.value} />
                </TableCellCondition>
              )}
            </DetailsSection>
          </DetailsTable>
        </div>
        <div className={styles['link-ml']}>
          <a
            href={`https://app.flagship.io/env/${fsEnvId}/report/ab/${campaignId}/details`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
            onClick={() => segmentTrack('Qa - Click edit targeting')}
          >
            {t('Edit use case')}

            <svg width="12px" height="12px" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_2754_59262)">
                <path
                  d="M6.81416 4.7056V5.9605H3.8512V12.8625H10.3697V9.7252H11.5549V13.4899C11.5549 13.6563 11.4925 13.8159 11.3813 13.9336C11.2702 14.0513 11.1195 14.1174 10.9623 14.1174H3.25861C3.10144 14.1174 2.95071 14.0513 2.83958 13.9336C2.72845 13.8159 2.66602 13.6563 2.66602 13.4899V5.33305C2.66602 5.16664 2.72845 5.00704 2.83958 4.88937C2.95071 4.7717 3.10144 4.7056 3.25861 4.7056H6.81416ZM13.3327 2.82324V7.84285H12.1475V4.96473L7.52942 9.85508L6.6915 8.96787L11.309 4.07814H8.59194V2.82324H13.3327Z"
                  fill="#007F91"
                />
              </g>
              <defs>
                <clipPath id="clip0_2754_59262">
                  <rect width="16" height="16.9412" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
